import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Check, Clock, Plus } from 'lucide-react';
import type { Commitment } from '../types';
import { getTodaysCommitments } from './TodaysFocus';
import InsetItem, { InsetMeta } from './InsetItem';

function frequencyLabel(frequency: Commitment['frequency']): string {
  switch (frequency) {
    case 'daily': return 'Daily';
    case 'weekly': return 'Weekly';
    case 'biweekly': return 'Every other week';
    case 'monthly': return 'Monthly';
    case 'one_time': return 'One-time';
    default: return '';
  }
}

function formatTime(timeStr: string | null): string {
  if (!timeStr) return '';
  const [hours, minutes] = timeStr.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return '';
  const period = hours >= 12 ? 'PM' : 'AM';
  return `${hours % 12 || 12}:${minutes.toString().padStart(2, '0')} ${period}`;
}

function formatDue(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function HomeGrowGoalsCard({
  commitments,
  onCheckIn,
  onUncheckIn,
  onComplete,
  onUncomplete,
}: {
  commitments: Commitment[];
  onCheckIn: (id: number, completed: boolean) => Promise<void>;
  onUncheckIn: (id: number) => Promise<void>;
  onComplete: (id: number) => Promise<void>;
  onUncomplete: (id: number) => Promise<void>;
}) {
  const [processingId, setProcessingId] = useState<number | null>(null);
  const todayStr = new Date().toISOString().split('T')[0];
  const dueToday = getTodaysCommitments(commitments);
  const dueIds = new Set(dueToday.filter((row) => !row.isCompletedToday).map((row) => row.commitment.id));
  const doneTodayIds = new Set(
    dueToday.filter((row) => row.isCompletedToday).map((row) => row.commitment.id),
  );

  const active = commitments.filter(
    (row) => row.status === 'pending' || row.status === 'in_progress',
  );
  const completedToday = commitments.filter((row) => {
    if (doneTodayIds.has(row.id)) return true;
    return (
      row.frequency === 'one_time'
      && row.status === 'completed'
      && row.completed_at
      && row.completed_at.slice(0, 10) === todayStr
    );
  });

  const preview = [...dueToday.filter((row) => !row.isCompletedToday).map((row) => row.commitment)];
  for (const goal of active) {
    if (preview.length >= 3) break;
    if (!preview.some((row) => row.id === goal.id)) preview.push(goal);
  }
  for (const goal of completedToday) {
    if (preview.length >= 3) break;
    if (!preview.some((row) => row.id === goal.id)) preview.push(goal);
  }

  const markDone = async (goal: Commitment) => {
    setProcessingId(goal.id);
    try {
      if (goal.frequency === 'one_time') {
        await onComplete(goal.id);
      } else {
        await onCheckIn(goal.id, true);
      }
    } finally {
      setProcessingId(null);
    }
  };

  const undo = async (goal: Commitment) => {
    setProcessingId(goal.id);
    try {
      if (goal.frequency === 'one_time' && goal.status === 'completed') {
        await onUncomplete(goal.id);
      } else {
        await onUncheckIn(goal.id);
      }
    } finally {
      setProcessingId(null);
    }
  };

  const isDone = (goal: Commitment) => (
    doneTodayIds.has(goal.id)
    || (
      goal.frequency === 'one_time'
      && goal.status === 'completed'
    )
  );

  return (
    <div className="bg-white rounded-2xl px-6 py-5 shadow-sm h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs font-medium uppercase tracking-wider text-gray-400">Grow goals</p>
        <Link to="/commitments" className="text-sm text-navy hover:underline">
          All goals
        </Link>
      </div>

      {preview.length === 0 ? (
        <div className="flex-1 flex flex-col">
          <p className="text-lg font-semibold text-navy">No active grow goals</p>
          <p className="text-sm text-gray-500 mt-1">
            Set one after forum, or whenever something needs a next step.
          </p>
          <Link
            to="/commitments?new=1"
            className="inline-flex items-center gap-1 mt-auto pt-4 text-sm font-medium text-navy hover:underline"
          >
            <Plus size={14} />
            Set a grow goal
          </Link>
        </div>
      ) : (
        <div className="flex-1 flex flex-col">
          {dueIds.size > 0 && (
            <p className="text-sm text-gray-500 mb-3">
              {dueIds.size === 1 ? '1 due today' : `${dueIds.size} due today`}
            </p>
          )}
          <div className="space-y-3">
            {preview.map((goal) => {
              const done = isDone(goal);
              const due = formatDue(goal.due_date);
              const time = formatTime(goal.preferred_time);
              return (
                <InsetItem
                  key={goal.id}
                  className={done ? 'opacity-80' : ''}
                  title={
                    <Link to="/commitments" className="hover:underline">
                      {goal.description}
                    </Link>
                  }
                  meta={
                    <>
                      {dueIds.has(goal.id) && !done ? (
                        <InsetMeta icon={Calendar}>Due today</InsetMeta>
                      ) : due ? (
                        <InsetMeta icon={Calendar}>Due {due}</InsetMeta>
                      ) : (
                        <InsetMeta>{frequencyLabel(goal.frequency)}</InsetMeta>
                      )}
                      {time && <InsetMeta icon={Clock}>{time}</InsetMeta>}
                      {goal.category_name && <InsetMeta>{goal.category_name}</InsetMeta>}
                      {done && <span className="text-green-700">Done</span>}
                    </>
                  }
                  action={
                    done ? (
                      <button
                        type="button"
                        onClick={() => undo(goal)}
                        disabled={processingId === goal.id}
                        className="px-3 py-2 text-sm text-gray-600 hover:bg-white rounded-lg transition disabled:opacity-50"
                      >
                        Undo
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => markDone(goal)}
                        disabled={processingId === goal.id}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-navy hover:bg-white rounded-lg transition disabled:opacity-50"
                      >
                        <Check size={14} />
                        Done
                      </button>
                    )
                  }
                />
              );
            })}
          </div>
          {active.length > preview.filter((goal) => !isDone(goal)).length && (
            <p className="text-xs text-gray-400 mt-3">
              +{active.length - preview.filter((goal) => !isDone(goal)).length} more
            </p>
          )}
          <Link to="/commitments" className="mt-auto pt-4 text-sm font-medium text-navy hover:underline">
            Open grow goals
          </Link>
        </div>
      )}
    </div>
  );
}
